import { useSuspenseQuery } from '@tanstack/react-query';
import { getInventoryData } from '../api/DashboardApi';
import { setMinimalPromiseTime } from 'src/common/utils/setMinimalPromiseTime';
import { dashboardQueries } from '../queries/dashboardQueries';
import { DatasetPoint } from 'src/common/components/Graphs/common/Graphs/AreaChart';
import { useMemo } from 'react';
import { getMonthName, getXAxisMonthLabels } from 'src/common/utils/chartAxisLabelsUtils';

const BORDER_BLUE_COLOR = '#407695';
const BACKGROUND_BLUE_COLOR = '#c6d6df';

type UseInventoryDataProps = {
  accountRef: string;
};

export const useInventoryData = ({ accountRef }: UseInventoryDataProps) => {
  const { data } = useSuspenseQuery<Awaited<ReturnType<typeof getInventoryData>>>({
    queryKey: dashboardQueries.inventory({
      accountRef,
    }),
    queryFn: () => setMinimalPromiseTime(getInventoryData({ accountRef })),
  });

  const xAxisLabels = getXAxisMonthLabels(data.data);

  const inventoryData = useMemo(() => {
    const dataset: DatasetPoint[] = data.data.map((item) => ({
      label: getMonthName(item.label, 'long'),
      value: item.value,
      borderColor: BORDER_BLUE_COLOR,
      backgroundColor: BACKGROUND_BLUE_COLOR,
      pointColor: BORDER_BLUE_COLOR,
      solidLine: true,
    }));

    return dataset;
  }, [data]);

  const datasets = [];

  if (inventoryData.length) {
    datasets.push({
      label: 'Inventory count',
      data: inventoryData,
      displayBorder: true,
      order: 1,
      fill: true,
    });
  }

  const yAxisLabel = data.unit.toLowerCase() === 'none' ? '' : data.unit;

  return { datasets, xAxisLabels, yAxisLabel };
};
